import React, { useState } from 'react';
import {
  Card, CardContent, Button, Typography, Box, Alert,
  LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import { publisherContentUploadApi, downloadTemplateApi } from '../../api/uploads';

const ACCEPTED_TYPES = '.xlsx,.xls,.csv,.pdf,.doc,.docx';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function UploadBooks() {
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a file.'); return; }
    setLoading(true); setError(''); setSubmitted(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await publisherContentUploadApi(fd);
      setSubmitted(true);
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplate = async () => {
    try {
      const res = await downloadTemplateApi();
      downloadBlob(res.data, 'book_upload_template.xlsx');
    } catch {
      // ignore
    }
  };

  return (
    <Layout title="Upload Content">
      <PageHeader
        title="Upload Book Content"
        breadcrumbs={[{ label: 'Publisher' }, { label: 'Upload Content' }]}
        action={
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleTemplate}>
            Download Excel Template
          </Button>
        }
      />

      <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Select & Submit File</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your book data as Excel, CSV, PDF, or Word document. Admin will review and publish on the portal.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {submitted && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: 2 }}
              >
                Your data has been successfully submitted. It will be reviewed and published by the Admin on the dashboard soon.
              </Alert>
            )}

            <Box component="form" onSubmit={handleUpload}>
              <Box sx={{
                border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                p: 4, textAlign: 'center', mb: 2, bgcolor: 'background.default',
              }}>
                <CloudUploadIcon sx={{ fontSize: 52, color: 'primary.main', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Supported formats: Excel (.xlsx, .xls), CSV, PDF, Word (.doc, .docx)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Maximum file size: 10 MB
                </Typography>
                <input
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={(e) => { setFile(e.target.files[0] || null); setSubmitted(false); }}
                  style={{ display: 'block', margin: '0 auto' }}
                />
                {file && (
                  <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                    Selected: {file.name}
                  </Typography>
                )}
              </Box>

              {loading && <LinearProgress sx={{ mb: 2 }} />}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || !file}
                startIcon={<CloudUploadIcon />}
              >
                {loading ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </Box>

          </CardContent>
        </Card>
    </Layout>
  );
}
